import { currentToken, renewedToken, SESSION_ENDED } from './auth/session.js'
import type {
  AuditEvent,
  CloudAccountRegistration,
  DelegationScope,
  BoundaryCoverage,
  BoundaryKind,
  InstanceRoleBoundaries,
  BoundaryMark,
  BoundaryRefusal,
  AuditFilter,
  DeploymentOutputs,
  CheckRun,
  DiagnosticBundle,
  Fleet,
  FleetFound,
  AccountAssessment,
  CloudOnboardingStepId,
  AccountInventory,
  AdoptionDetails,
  OnboardingAssessment,
  BudgetEvaluation,
  BudgetInput,
  CostSeriesPoint,
  DestroyedOutsideMark,
  PlatformBudget,
  PreflightResult,
  RunCommand,
  RunLogSummary,
  RunSummaryHeadline,
  ScanSaysGone,
  TenantKind,
} from '@qim/shared'

export interface Me {
  readonly principalId: string
  readonly email: string
  readonly role: string
  readonly isStaff: boolean
  readonly scope:
    { readonly kind: 'all' } | { readonly kind: 'tenants'; readonly tenantIds: readonly string[] }
}

export class NotAuthenticated extends Error {}

/**
 * A refusal the API explained.
 *
 * Distinct from a transport failure, because the two need different words: one
 * is "the customer's grant expired, extend it" and the other is "try again".
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** The run record the API returned with a refusal, when it returned one. */
    readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const nonEmpty = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value : undefined

/**
 * The best sentence available for why a call failed (IM-159).
 *
 * The API explains itself in two shapes, and every view used to read at most
 * one of them. A run the customer's account refused comes back as the run
 * record, with the reason under `failure`; a refusal at the chokepoint — an
 * account set not to take new instances, the platform budget — has no run
 * record and answers `{ error, detail }` at the top level. The run form read
 * only the first, so a blocked apply said "Request failed (403)" while the API
 * had named who blocked it and when. `access.tsx` had fixed the same thing
 * inline for its own screen, which is how one bug became several.
 *
 * In order: the account's refusal, the API's sentence, the API's reason code,
 * and the transport message only when the body said nothing. Never `''` — a
 * network error or a 502 with no body still has to put words on the screen,
 * and an empty alert reads as though nothing went wrong.
 */
export const explainFailure = (error: unknown): string => {
  if (error instanceof ApiError) {
    const body = error.body as
      { failure?: { detail?: unknown }; detail?: unknown; error?: unknown } | null | undefined
    const said = nonEmpty(body?.failure?.detail) ?? nonEmpty(body?.detail) ?? nonEmpty(body?.error)
    if (said !== undefined) return said
  }
  return (
    (error instanceof Error ? nonEmpty(error.message) : undefined) ??
    'The control plane could not be reached.'
  )
}

const send = async <T>(baseUrl: string, path: string, init?: RequestInit): Promise<T> => {
  /*
   * One renewal before giving up (IM-170). A timer renews ahead of expiry, but
   * a tab that slept through it wakes with a stale token, and the first sign
   * used to be an Apply that failed with "No valid session". If the renewal is
   * refused the session has ended, the header has been told, and this says the
   * same thing rather than a phrase nobody can act on.
   */
  const token = currentToken() ?? (await renewedToken())
  if (token === undefined) throw new NotAuthenticated(SESSION_ENDED)

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${token}`,
      ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }),
    },
  })

  if (response.status === 401) throw new NotAuthenticated('Session rejected')
  if (!response.ok) {
    // The API deliberately returns the same 404 for a tenant out of scope and
    // one that does not exist, so this message must not speculate about which.
    let body: unknown
    try {
      body = await response.json()
    } catch {
      body = undefined
    }
    throw new ApiError(response.status, `Request failed (${String(response.status)})`, body)
  }
  return (await response.json()) as T
}

const request = <T>(baseUrl: string, path: string): Promise<T> => send<T>(baseUrl, path)

/**
 * `?includeHidden=1`, when the viewer has asked to see hidden records.
 *
 * A preference, not a permission: the API decides what they may see from their
 * scope and this can only widen which of those rows come back. Without it there
 * is no way to reach a hidden record, and therefore no way to restore one.
 */
const hiddenQuery = (includeHidden?: boolean): string =>
  includeHidden === true ? '?includeHidden=1' : ''

/** Hiding and restoring (IM-108). `DELETE` writes a mark; nothing is deleted. */
export const hideTenant = (baseUrl: string, tenantId: string): Promise<unknown> =>
  send(baseUrl, `/tenants/${encodeURIComponent(tenantId)}`, { method: 'DELETE' })

export const restoreTenant = (baseUrl: string, tenantId: string): Promise<unknown> =>
  send(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/restore`, { method: 'POST' })

export const hideAccount = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<unknown> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}`,
    { method: 'DELETE' },
  )

export const restoreAccount = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<unknown> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/restore`,
    { method: 'POST' },
  )

export const hideInstance = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
): Promise<unknown> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/deployments/${encodeURIComponent(deploymentId)}`,
    { method: 'DELETE' },
  )

/**
 * Marking an instance destroyed outside the control plane, and withdrawing the
 * mark (IM-156). `records:manage`. The API checks the scan agrees; the console
 * only offers it when the scan already said so.
 */
export const markDestroyedOutside = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
  withdraw = false,
): Promise<unknown> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/deployments/${encodeURIComponent(deploymentId)}/destroyed-outside`,
    { method: withdraw ? 'DELETE' : 'POST' },
  )

export const hideRegion = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  region: string,
): Promise<unknown> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/regions/${encodeURIComponent(region)}`,
    { method: 'DELETE' },
  )

export const restoreRegion = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  region: string,
): Promise<unknown> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/regions/${encodeURIComponent(region)}/restore`,
    { method: 'POST' },
  )

export const restoreInstance = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
): Promise<unknown> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/deployments/${encodeURIComponent(deploymentId)}/restore`,
    { method: 'POST' },
  )

export const fetchMe = (baseUrl: string): Promise<Me> => request<Me>(baseUrl, '/me')

export interface TenantSummary {
  readonly tenantId: string
  readonly name: string
  readonly createdAt: string
  /** A customer's, or Qrvey's own (IM-149). Fixed at creation. */
  readonly kind: TenantKind
  /** Present means hidden. Only ever returned when `includeHidden` was asked for. */
  readonly deletedAt?: string
  readonly deletedBy?: string
}

export const fetchTenants = (
  baseUrl: string,
  includeHidden?: boolean,
): Promise<{ tenants: readonly TenantSummary[] }> =>
  request(baseUrl, `/tenants${hiddenQuery(includeHidden)}`)

/** A setting's current value and who decided it (IM-127). */
export interface AccountSettingMark {
  readonly value: boolean
  readonly at: string
  readonly by: string
}

/**
 * What a cloud account is, and what it will accept.
 *
 * Each absent until somebody sets it, and each written as `false` rather than
 * removed when turned off — so "who unblocked this account, and when" has an
 * answer.
 */
export interface AccountSettingsWire {
  readonly newInstancesBlocked?: AccountSettingMark
}

export interface AccountSummary {
  readonly accountKey: string
  readonly label: string
  readonly cloud: string
  readonly awsAccountId?: string
  /** Azure's identity, the counterpart of `awsAccountId` (IM-44). */
  readonly subscriptionId?: string
  readonly azureTenantId?: string
  readonly delegationScope?: DelegationScope
  readonly deletedAt?: string
  readonly deletedBy?: string
  readonly settings?: AccountSettingsWire
}

export const fetchAccounts = (
  baseUrl: string,
  tenantId: string,
  includeHidden?: boolean,
): Promise<{ accounts: readonly AccountSummary[] }> =>
  request(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/accounts${hiddenQuery(includeHidden)}`)

/** Mirrors `AccountHealth` in the cloud port. Three states, each actionable. */
export type AccountHealth =
  | { readonly status: 'not-onboarded'; readonly detail: string; readonly nextStep: string }
  | {
      readonly status: 'ready'
      readonly bootstrapVersion: string
      readonly compatibility: { readonly state: string; readonly message?: string }
    }
  | {
      readonly status: 'needs-attention'
      readonly detail: string
      readonly nextStep: string
      readonly bootstrapVersion?: string
    }

export const fetchAccountHealth = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<{ accountKey: string; label: string; health: AccountHealth }> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/health`,
  )

export interface RunSummary {
  readonly runId: string
  readonly command: string
  readonly state: string
  readonly requestedBy: string
  readonly requestedAt: string
  readonly chartVersion?: string
  /** The installer image that ran it, recorded at completion (IM-195). */
  readonly installerImage?: string
  readonly installerImageDigest?: string
  /** The installer tag it asked for, when not the region's own (IM-197). */
  readonly installerTag?: string
  readonly failure?: { readonly reason: string; readonly detail: string }
  /*
   * Present on a single-run read, which returns the stored record whole.
   *
   * Optional rather than required because this type is also the shape of the
   * `lastRun` carried on a deployment summary, and widening it there would claim
   * fields that projection does not promise. A run screen shows them when they
   * are there and omits the row when they are not — which is also the honest
   * answer for a run recorded before the field existed.
   */
  readonly accountKey?: string
  readonly deploymentId?: string
  readonly region?: string
  /** What this run was asked to deploy, secrets removed. On a single-run read. */
  readonly variables?: Readonly<Record<string, unknown>>
  /** Who had to agree to this run, and what they said (IM-102, IM-39). */
  readonly approval?: RunApprovalWire
  /**
   * The headline of its log summary, once somebody has opened the finished
   * run (IM-35). On the record, so a history row can show it with no read.
   */
  readonly summaryHeadline?: RunSummaryHeadline
  /**
   * The run asked for the ECR image cache and was sent Qrvey's registry
   * instead (MP-3601), with the reason. On a single-run read.
   */
  readonly imagePull?: { readonly requested: string; readonly sent: string; readonly why: string }
}

/** `Run.approval`, as it arrives. Strings, so an unknown outcome renders raw. */
export interface RunApprovalWire {
  readonly requirement: string
  readonly expiresAt?: string
  /** Why it waits, in the gate's words (IM-189). Absent on older runs. */
  readonly why?: string
  readonly decision?: {
    readonly outcome: string
    readonly at: string
    readonly by: string
    readonly reason?: string
  }
}

/** The newest run against an instance still waiting for somebody to agree. */
export interface PendingApprovalWire {
  readonly runId: string
  readonly command: string
  readonly requestedBy: string
  readonly requestedAt: string
  readonly requirement: string
  readonly expiresAt?: string
  /** Why it waits, in the gate's words (IM-189). Absent on older runs. */
  readonly why?: string
}

/** `DeploymentSummary.search`, as it arrives. */
export interface SearchBackendWire {
  readonly backend: string
  readonly size?: string
  readonly basis: string
}

export interface DeploymentSummary {
  readonly deploymentId: string
  readonly accountKey: string
  readonly status: string
  /** Present when hidden. Only returned when `includeHidden` was asked for. */
  readonly hidden?: { readonly at: string; readonly by: string; readonly status: string }
  readonly chartVersion?: string
  /** The installer that applied `chartVersion`, from the same run (IM-195). */
  readonly installer?: { readonly image: string; readonly digest?: string }
  readonly region?: string
  /**
   * Its search backend, from the newest succeeded apply or — `basis:
   * 'requested'` — the newest request when nothing has been applied (IM-182).
   * Strings, so a backend this build does not know renders raw.
   */
  readonly search?: SearchBackendWire
  /** The last non-secret variables this deployment ran with. Never the key. */
  readonly variables?: Readonly<Record<string, unknown>>
  /**
   * How long this instance was meant to live, when somebody has said (IM-134).
   *
   * Absent means nobody has answered — a third state beside within and over,
   * and the one whose useful action is to set one.
   */
  readonly lifetime?: {
    readonly expiresAt: string | null
    readonly asked:
      | { readonly kind: 'days'; readonly days: number }
      | { readonly kind: 'until'; readonly date: string }
      | { readonly kind: 'indefinite' }
    readonly at: string
    readonly by: string
  }
  /**
   * When this instance became live, and has been continuously since (IM-129).
   *
   * Absent when nothing is live — `never-applied` and `destroyed` — which the
   * screens render as a dash rather than a zero or a blank cell: one reads as
   * brand new and the other as a rendering fault.
   */
  readonly liveSince?: string
  /**
   * The last run against this instance, if there has been one.
   *
   * **Optional since IM-140**: an adopted instance was alive before this
   * control plane heard of it and has no runs here, so there is genuinely
   * nothing to show until somebody runs something. A screen shows when it was
   * brought in instead.
   */
  readonly lastRun?: RunSummary
  /** Present when this instance was brought in rather than deployed here. */
  readonly adopted?: { readonly at: string; readonly by: string }
  /**
   * Somebody marked it destroyed outside the control plane (IM-156). Present
   * only while it stands, which is also when `status` reads `destroyed`.
   */
  readonly destroyedOutside?: DestroyedOutsideMark
  /**
   * A scan newer than the last run could not find its cluster (IM-156). A
   * prompt to mark it, never a verdict — and only on a live or failed instance.
   */
  readonly scanSaysGone?: ScanSaysGone
  /** A run against it waiting for approval (IM-102, IM-39). Not `in-progress`. */
  readonly awaitingApproval?: PendingApprovalWire
  /** Standing protection: a destroy needs a second person (IM-39). */
  readonly protected?: { readonly at: string; readonly by: string }
  /**
   * You removed its protection recently, so a destroy *you* start still waits
   * for a second approver (IM-189). Shows no lock; set by the API per caller.
   */
  readonly protectionRemovedByYou?: { readonly removedAt: string; readonly until: string }
}

export const fetchDeployments = (
  baseUrl: string,
  tenantId: string,
  includeHidden?: boolean,
): Promise<{ deployments: readonly DeploymentSummary[] }> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/deployments${hiddenQuery(includeHidden)}`,
  )

/**
 * Every instance the caller may see, in one read.
 *
 * Derived from this control plane's own records, so it is cheap enough for the
 * landing screen. The scoping happened server-side — `visibleTenants` — and the
 * console does no filtering of its own, for the same reason it never has: a
 * second filter is a place for the two to disagree.
 */
/**
 * Every instance the caller may see, **every kind of customer** — the ⌘K
 * palette's read.
 *
 * Deliberately unfiltered: a support engineer jumps to a *customer's* instance
 * from the palette, so it must keep reaching them. The Fleet screen reads
 * `fetchQrveyFleet` instead. Two named functions rather than an optional
 * argument, so which read a caller makes is visible at the call site.
 */
export const fetchFleet = (baseUrl: string): Promise<Fleet> => request(baseUrl, '/fleet')

/**
 * Qrvey's own instances only — the Fleet screen's read.
 *
 * Filtered by the API (`?kind=qrvey`), not here: filtering in the browser would
 * still send every customer's instances to a screen that shows none of them.
 */
export const fetchQrveyFleet = (baseUrl: string): Promise<Fleet> =>
  request(baseUrl, '/fleet?kind=qrvey')

/**
 * What the last scan of each of Qrvey's own accounts found that nobody here
 * manages (IM-101). Kept scans only, so it is cheap — but it is
 * `diagnostics:read`, so the Fleet asks only when the role holds that.
 */
export const fetchQrveyFound = (baseUrl: string): Promise<FleetFound> =>
  request(baseUrl, '/fleet/found?kind=qrvey')

export interface Assignment {
  readonly email: string
  readonly role: string
  readonly scope:
    { readonly kind: 'all' } | { readonly kind: 'tenants'; readonly tenantIds: readonly string[] }
  readonly updatedAt: string
  readonly updatedBy: string
  /** Present when access was taken away. The record stays; the access does not. */
  readonly revokedAt?: string
}

export interface AccessList {
  readonly assignments: readonly Assignment[]
  /**
   * Break-glass accounts, which have no assignment and cannot be given one here.
   *
   * Returned so the screen can say they exist and are configuration rather than
   * omitting them and implying they do not.
   */
  readonly breakGlassEmails: readonly string[]
}

export const fetchAccess = (baseUrl: string): Promise<AccessList> => request(baseUrl, '/access')

export const putAssignment = (
  baseUrl: string,
  assignment: {
    readonly email: string
    readonly role: string
    readonly scopeKind: 'all' | 'tenants'
    readonly tenantIds?: readonly string[]
  },
): Promise<unknown> =>
  send(baseUrl, '/access', { method: 'POST', body: JSON.stringify(assignment) })

/** One customer's own users, and the Qrvey staff with access to them (IM-153). */
export interface CustomerUsers {
  readonly users: readonly {
    readonly email: string
    readonly role: 'customer-admin' | 'customer-viewer'
    readonly updatedAt: string
    readonly updatedBy: string
    readonly revokedAt?: string
  }[]
  /** Staff Qrvey scoped to this customer specifically. Read-only. */
  readonly staff: readonly { readonly email: string; readonly role: string }[]
  /** Staff with access to every customer, counted rather than named. */
  readonly staffWithAllCustomers: number
}

const usersPath = (tenantId: string): string => `/tenants/${encodeURIComponent(tenantId)}/users`

export const fetchCustomerUsers = (baseUrl: string, tenantId: string): Promise<CustomerUsers> =>
  request(baseUrl, usersPath(tenantId))

/** Invites or changes a user. `invitation` says whether an email went out. */
export const inviteCustomerUser = (
  baseUrl: string,
  tenantId: string,
  email: string,
  role: 'customer-admin' | 'customer-viewer',
): Promise<{ invitation: 'sent' | 'already-has-sign-in' | 'not-sent' }> =>
  send(baseUrl, usersPath(tenantId), { method: 'POST', body: JSON.stringify({ email, role }) })

/** Takes a customer user's access away. Stored as a revocation; nothing is deleted. */
export const removeCustomerUser = (
  baseUrl: string,
  tenantId: string,
  email: string,
): Promise<unknown> =>
  send(baseUrl, `${usersPath(tenantId)}/${encodeURIComponent(email)}`, { method: 'DELETE' })

/** Takes access away. Writes a revoked assignment; nothing is deleted. */
export const revokeAssignment = (baseUrl: string, email: string): Promise<unknown> =>
  send(baseUrl, `/access/${encodeURIComponent(email)}`, { method: 'DELETE' })

export interface CreatedTenant {
  readonly tenantId: string
  readonly name: string
  readonly createdAt: string
  readonly kind: TenantKind
}

/** Creating a customer. `tenant:manage`. The kind is required and cannot be changed later. */
export const createTenant = (
  baseUrl: string,
  tenantId: string,
  name: string,
  kind: TenantKind,
): Promise<CreatedTenant> =>
  send(baseUrl, '/tenants', { method: 'POST', body: JSON.stringify({ tenantId, name, kind }) })

export interface RegisteredAccountResult {
  readonly account: AccountSummary
  /**
   * AWS only. Returned by this call and by nothing else, ever.
   *
   * The API mints it here because this is the moment it has to be handed to the
   * customer for their trust policy. There is no read path for it afterwards —
   * the listing projects columns that exclude the ciphertext, and recovering one
   * is deliberately unbuilt. So the screen that receives this has one chance to
   * put it in front of a person, and has to say so.
   */
  readonly externalId?: string
  /** How the control plane will address itself into the account, read-only. */
  readonly diagnosticsPrincipal: string
}

/**
 * Registering a cloud account. `account:onboard`. It mints the external ID on
 * AWS, and on Azure a delegation mark the template carries and nobody copies.
 */
export const registerAccount = (
  baseUrl: string,
  tenantId: string,
  registration: CloudAccountRegistration,
): Promise<RegisteredAccountResult> =>
  send(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/accounts`, {
    method: 'POST',
    body: JSON.stringify(registration),
  })

export interface AuditPage {
  readonly days: readonly string[]
  readonly events: readonly AuditEvent[]
}

export interface AuditDenialPage {
  readonly days: readonly string[]
  readonly denials: readonly AuditEvent[]
}

/**
 * Builds the query string for an audit read.
 *
 * Only keys with a value are sent: an empty `actor=` would be a filter matching
 * nothing rather than no filter, and the API treats absent and empty
 * differently on purpose.
 */
const auditQueryString = (filter: AuditFilter): string => {
  const params = new URLSearchParams()
  if (filter.from !== undefined) params.set('from', filter.from)
  if (filter.to !== undefined) params.set('to', filter.to)
  if (filter.tenantId !== undefined && filter.tenantId !== '') {
    params.set('tenantId', filter.tenantId)
  }
  if (filter.actor !== undefined && filter.actor !== '') params.set('actor', filter.actor)
  if (filter.capability !== undefined) params.set('capability', filter.capability)
  if (filter.deniedOnly === true) params.set('deniedOnly', 'true')
  const query = params.toString()
  return query === '' ? '' : `?${query}`
}

export const fetchAudit = (baseUrl: string, filter: AuditFilter): Promise<AuditPage> =>
  request(baseUrl, `/audit${auditQueryString(filter)}`)

/**
 * One customer's trail — the customer screen's Audit tab.
 *
 * The tenant-scoped route rather than `/audit?tenantId=`: it is authorized
 * against the customer in the path, and it answers "who touched *this*
 * customer", leaving out untenanted refusals by design. The tenant is the
 * path's, so a `tenantId` in the filter is dropped rather than sent.
 */
export const fetchTenantAudit = (
  baseUrl: string,
  tenantId: string,
  filter: AuditFilter,
): Promise<AuditPage> => {
  const { tenantId: _ignored, ...rest } = filter
  return request(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/audit${auditQueryString(rest)}`)
}

/**
 * Refusals only.
 *
 * A separate endpoint rather than a filter, because the store reads them from a
 * sparse index — a filter over a busy day would lose a quiet trickle of denials
 * behind a flood of allows, which is the signal the trail exists for.
 */
export const fetchAuditDenials = (baseUrl: string, filter: AuditFilter): Promise<AuditDenialPage> =>
  request(baseUrl, `/audit/denials${auditQueryString(filter)}`)

export interface AccountOnboarding {
  readonly accountKey: string
  readonly label: string
  /** The "Permissions boundaries" step (IM-194). Absent from an older API. */
  readonly boundaries?: BoundaryCoverage
  /**
   * What this account is, and what it accepts (IM-127).
   *
   * On this read rather than fetched separately: the state is a fact anyone
   * holding `account:read` may see, and only changing it needs
   * `account:settings`. Optional so a console served against an older API still
   * renders.
   */
  readonly settings?: AccountSettingsWire
  /** The owning customer's kind (IM-149), for the badge beside the account's name. */
  readonly tenantKind?: TenantKind
  /**
   * The account read as a whole, which is what the screen showed before
   * regions existed.
   *
   * Kept while the per-region view beds in. It should go once nothing renders
   * it — a second answer to the same question is a place for two answers to
   * disagree, which is the thing `account` below exists to avoid.
   */
  readonly assessment: OnboardingAssessment<CloudOnboardingStepId>
  /**
   * The account's own steps, and one assessment per region it is set up in.
   *
   * Optional only so a console served against an API that predates IM-95 still
   * renders. Treat its absence as "this API cannot answer per region", not as
   * "this account has no regions" — those are different, and the second is a
   * real and common state that arrives as an empty `regions` array.
   */
  readonly account?: AccountAssessment<CloudOnboardingStepId>
}

/**
 * Every onboarding step for one account, already assessed by the API.
 *
 * The console renders the answer rather than deciding it: the expectations are
 * the control plane's own constants, and a second judgement here is a place for
 * the two to disagree about whether a customer is ready.
 */
/**
 * What an account spent, and what the estate spent (IM-130).
 *
 * Read on demand, never on render: `GetCostAndUsage` charges the **customer's**
 * account roughly a cent a call. That is why these are functions a screen calls
 * when somebody asks, rather than fields on the reads it already makes — and why
 * `Fleet` carries no cost at all.
 */
export interface CostPeriodWire {
  readonly from: string
  readonly to: string
  /** Inclusive, in English. The API decrements the exclusive end once, for all. */
  readonly describes?: string
}

export type AccountCostWire =
  | {
      readonly state: 'read'
      readonly from: string
      readonly to: string
      readonly amount: number
      readonly currency: string
      readonly readAt: string
    }
  /** Never rendered as zero — see the note on the panel. */
  | { readonly state: 'unavailable'; readonly reason: string }

/** Recording how long an instance is meant to live (IM-134). */
export const setLifetime = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
  lifetime: { days: number } | { until: string } | { indefinite: true },
): Promise<unknown> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(
      accountKey,
    )}/deployments/${encodeURIComponent(deploymentId)}/lifetime`,
    { method: 'POST', body: JSON.stringify({ lifetime }) },
  )

export const fetchAccountCost = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<{ period: CostPeriodWire; cost: AccountCostWire }> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/cost`,
  )

/** One instance's share, from the `qrvey-id` tag (IM-131). */
export interface InstanceCostLineWire {
  readonly deploymentId: string
  readonly amount: number
}

export type InstanceCostsWire =
  | {
      readonly state: 'read'
      readonly from: string
      readonly to: string
      readonly currency: string
      readonly instances: readonly InstanceCostLineWire[]
      /** Spend AWS could not attribute to an instance. Its own row, always. */
      readonly unattributed: number
      readonly total: number
      /** True when AWS had more groups than one page held. */
      readonly truncated?: boolean
      readonly readAt: string
    }
  /** Readable, but nothing breaks down. Never rendered as zero. */
  | { readonly state: 'not-attributed'; readonly reason: string }
  | { readonly state: 'unavailable'; readonly reason: string }

export const fetchInstanceCosts = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<{ period: CostPeriodWire; costs: InstanceCostsWire }> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(
      accountKey,
    )}/cost/instances`,
  )

/*
 * `GET /cost` — every visible account's month, customers' included — had its
 * console panel here until the secondary navigation. The Fleet became Qrvey's
 * own and its Cost tab reads `/cost/daily`; a customer's cost is on that
 * customer's Cost tab. The route stays for anyone who wants the whole estate.
 */

/**
 * Qrvey's own month so far, per day, for the fleet's Cost tab chart. Qrvey-owned
 * accounts only, by construction on the API side — it is drawn against the
 * platform budget, which counts nothing else.
 */
export interface DailyFleetCost {
  readonly period: CostPeriodWire
  readonly days: readonly CostSeriesPoint[]
  readonly accounts: readonly {
    readonly tenantId: string
    readonly tenantName: string
    readonly accountKey: string
    readonly label: string
    readonly amount: number
    readonly readAt: string
  }[]
  /** Named, never drawn as zero. */
  readonly unreadable: readonly {
    readonly tenantId: string
    readonly tenantName: string
    readonly accountKey: string
    readonly label: string
    readonly reason: string
  }[]
  readonly total: number
  readonly currency: string
}

export const fetchDailyFleetCost = (baseUrl: string): Promise<DailyFleetCost> =>
  request(baseUrl, '/cost/daily')

/**
 * The platform budget over Qrvey's own fleet (IM-152), with this month's
 * evaluation and whether new instances are being refused right now.
 */
export interface BudgetStatus {
  readonly budget?: PlatformBudget
  readonly evaluation?: BudgetEvaluation
  readonly blocking: boolean
}

export const fetchBudget = (baseUrl: string): Promise<BudgetStatus> => request(baseUrl, '/budget')

/** Setting it. `budget:manage`, platform-admin only. */
export const setBudget = (
  baseUrl: string,
  input: BudgetInput,
): Promise<{ budget: PlatformBudget }> =>
  send(baseUrl, '/budget', { method: 'POST', body: JSON.stringify(input) })

/**
 * Changing what an account is, or what it accepts (IM-127).
 *
 * A partial update: a setting this does not name is left alone, so a screen
 * offering one switch cannot silently reset the other.
 */
export const setAccountSettings = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  update: Partial<Record<'newInstancesBlocked', boolean>>,
): Promise<{ accountKey: string; settings: AccountSettingsWire }> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/settings`,
    { method: 'POST', body: JSON.stringify(update) },
  )

export const fetchAccountOnboarding = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<AccountOnboarding> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(
      accountKey,
    )}/onboarding`,
  )

/**
 * The four artifacts a customer deploys: access, grant, region, dispatcher.
 *
 * Four rather than two since IM-93 and IM-94 split the single access stack, so
 * that a second region needs one new stack instead of a rewrite of the one
 * holding the trust boundary.
 *
 * **Only the dispatcher fetch is more than a read.** It also grants the account
 * permission to publish onto Qrvey's run bus, so re-fetching repairs that
 * wiring — which is why the request is made where the artifact that does the
 * reporting is handed over, and nowhere else. The screen says so at the
 * download. The other three have no side effect at all.
 */
export const fetchBootstrapTemplate = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  /** Offered requiring each deployment's boundary (IM-194); refused until every instance has one. */
  requireBoundary = false,
): Promise<{ readonly template: unknown }> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/bootstrap${
      requireBoundary ? '?boundary=required' : ''
    }`,
  )

/** Whether each instance's roles carry its boundary, asked of IAM (IM-194). */
export type RoleBoundariesView =
  | {
      readonly state: 'read'
      readonly instances: readonly (InstanceRoleBoundaries & { readonly recorded: boolean })[]
    }
  | { readonly state: 'unreadable'; readonly reason: string }

export const fetchRoleBoundaries = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<RoleBoundariesView> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/boundaries/roles`,
  )

/** The runner role and the deployment window. One per account, all regions. */
export const fetchGrantTemplate = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<{ readonly template: unknown }> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/grant`,
  )

/**
 * Somewhere to keep a deployment's state. One per region.
 *
 * Takes no region, because the template does not: every resource in it is
 * region-local and lands wherever the customer creates the stack. Which region
 * that should be is said in the instruction beside the download, not encoded in
 * the request.
 */
export const fetchRegionTemplate = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<{ readonly template: unknown }> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/region`,
  )

/**
 * Any other artifact an adapter declares (IM-45's `…/artifacts/{kind}`): on
 * Azure, the deploy-access script, the runner template and an instance's
 * template. The file name is the API's, since a script is not a `.json`.
 */
export const fetchArtifact = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  kind: string,
): Promise<{ readonly filename?: string; readonly template: unknown }> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(
      accountKey,
    )}/artifacts/${encodeURIComponent(kind)}`,
  )

export const fetchDispatcherTemplate = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<{ readonly template: unknown }> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/dispatcher`,
  )

export interface AccountInstances {
  readonly accountKey: string
  readonly label: string
  /**
   * What the last scan found, and when it ran.
   *
   * Absent means nobody has ever scanned this account — which is not the same
   * as a scan that found nothing, and the screen says so differently.
   */
  readonly inventory?: AccountInventory
  /**
   * The account could not be read at all.
   *
   * Distinct from an empty list, and the distinction is the point: on this
   * screen an empty list reads as a clean account, which is the one answer a
   * failed scan must never give. A kept list is still returned alongside it,
   * because a failed scan must not cost the answer we already had.
   */
  readonly unreachable?: boolean
  /**
   * The standing protection marks in this account (IM-174), from our own store
   * rather than the scan — protection is a fact about our records, and the scan
   * reads the customer's account. One query beside the kept list, not per row.
   */
  readonly protections?: readonly {
    readonly deploymentId: string
    readonly at: string
    readonly by: string
  }[]
}

/**
 * The kept list. One read of Qrvey's own store, no cross-account call.
 *
 * Cheap enough for page load, which is the whole reason the scan is stored.
 */
export const fetchInstances = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<AccountInstances> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/instances`,
  )

/**
 * Re-read the customer's account and replace the kept list.
 *
 * Several cross-account calls — the most expensive read in the product — so it
 * happens when someone asks and never on render.
 */
export const rescanInstances = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
): Promise<AccountInstances> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(
      accountKey,
    )}/instances/scan`,
    { method: 'POST' },
  )

/**
 * Claims a discovered instance, with the non-secret half of its config.
 *
 * The file is parsed in the browser and only these five fields travel — see
 * `readAdoptionConfig`. The credentials in a `config.json` never leave the
 * machine it was opened on.
 */
export const adoptInstance = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  details: AdoptionDetails,
): Promise<{ readonly adopted: string }> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/adoptions`,
    { method: 'POST', body: JSON.stringify(details) },
  )

export interface DeploymentDetail extends DeploymentSummary {
  readonly history: readonly RunSummary[]
  /** Present once someone has read a run's log. See `fetchDeploymentOutputs`. */
  readonly outputs?: DeploymentOutputs
  /** The standing permissions boundary every run carries (IM-192). */
  readonly boundary?: BoundaryMark
  /**
   * Where the instance keeps its Terraform state, when recorded: by the
   * newest run that carried it, or by its adoption (IM-139, IM-140). Read by
   * the state access panel (IM-214).
   */
  readonly stateBucket?: string
  readonly stateKey?: string
}

export interface DeploymentOutputsResponse {
  readonly deploymentId: string
  /** `null` when no run has printed a banner yet, with `note` saying so. */
  readonly outputs: DeploymentOutputs | null
  readonly note?: string
}

/**
 * Reads a deployment's endpoints out of its newest successful run's log.
 *
 * A cross-account call, so it is not made on page load: a deployment that has
 * already had one carries the answer on its record, and this is the explicit
 * refresh. Getting *newer* values than the last run printed means running
 * `output` again, which this screen already offers.
 */
export const fetchDeploymentOutputs = (
  baseUrl: string,
  tenantId: string,
  deploymentId: string,
): Promise<DeploymentOutputsResponse> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/deployments/${encodeURIComponent(
      deploymentId,
    )}/outputs`,
  )

/**
 * Collects a diagnostic bundle for one instance (IM-54).
 *
 * Several cross-account reads, so it is made only when somebody asks — and it
 * is audited on the API side with what came back. The account key is in the
 * path because a deployment id is unique only within an account.
 */
export const fetchDiagnosticBundle = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
): Promise<DiagnosticBundle> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(
      accountKey,
    )}/deployments/${encodeURIComponent(deploymentId)}/diagnostics`,
  )

const checksPath = (tenantId: string, accountKey: string, deploymentId: string): string =>
  `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(
    accountKey,
  )}/deployments/${encodeURIComponent(deploymentId)}/checks`

/** An instance's kept check runs, newest first (IM-199). Reads nothing from its account. */
export const fetchCheckRuns = async (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
): Promise<readonly CheckRun[]> =>
  (
    await request<{ readonly runs: readonly CheckRun[] }>(
      baseUrl,
      checksPath(tenantId, accountKey, deploymentId),
    )
  ).runs

/**
 * Runs the standard checks now (IM-199): a few reads of the customer's account
 * through the diagnostics role, answered with the finished run.
 */
export const runChecks = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
): Promise<CheckRun> =>
  send(baseUrl, checksPath(tenantId, accountKey, deploymentId), { method: 'POST' })

/**
 * An instance's permissions boundary, as the API checked it just now (IM-192).
 *
 * `check` is the API's own `checkBoundary` answer — the console renders it and
 * decides nothing — and `offer` is present exactly when the installer has
 * published a document to offer.
 */
export interface BoundaryView {
  readonly deploymentId: string
  readonly region: string
  /** No runs or adoption yet: the expected document is this console's render. */
  readonly isNew: boolean
  /** The pinned template's hash, and the one the installer published, when it did. */
  readonly template: {
    readonly pinned: string
    readonly published?: string
    readonly matches?: boolean
  }
  readonly expectedArn: string
  readonly recorded: BoundaryMark | null
  readonly published:
    | { readonly state: 'found'; readonly kind: BoundaryKind }
    | { readonly state: 'absent' }
    | { readonly state: 'unreadable'; readonly reason: string }
  readonly live:
    | { readonly state: 'found'; readonly arn: string }
    | { readonly state: 'absent' }
    | { readonly state: 'unreadable'; readonly reason: string }
  readonly check: { readonly state: 'match' } | ({ readonly state: 'refused' } & BoundaryRefusal)
  readonly offer?: {
    readonly source: 'published' | 'rendered'
    readonly kind: BoundaryKind
    readonly document: unknown
    readonly artifact: { readonly filename: string; readonly document: unknown }
    readonly command: string
  }
}

const boundaryPath = (tenantId: string, accountKey: string, deploymentId: string): string =>
  `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(
    accountKey,
  )}/deployments/${encodeURIComponent(deploymentId)}/boundary`

/**
 * `region` only for an instance that does not exist yet: the region its first
 * apply will build it in, which its boundary names.
 */
export const fetchBoundary = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
  region?: string,
): Promise<BoundaryView> =>
  request(
    baseUrl,
    `${boundaryPath(tenantId, accountKey, deploymentId)}${
      region === undefined ? '' : `?region=${encodeURIComponent(region)}`
    }`,
  )

/** Records the boundary the account holds, or is refused with the API's reasons. */
export const recordBoundary = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
  region?: string,
): Promise<{ readonly recorded: BoundaryMark }> =>
  send(baseUrl, boundaryPath(tenantId, accountKey, deploymentId), {
    method: 'POST',
    body: JSON.stringify(region === undefined ? {} : { region }),
  })

/** Clears it: a stored state, never a delete. */
export const clearBoundary = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
): Promise<{ readonly recorded: null; readonly note?: string }> =>
  send(baseUrl, boundaryPath(tenantId, accountKey, deploymentId), { method: 'DELETE' })

export const fetchDeployment = (
  baseUrl: string,
  tenantId: string,
  deploymentId: string,
): Promise<DeploymentDetail> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/deployments/${encodeURIComponent(deploymentId)}`,
  )

/**
 * Asks the customer's account to do something.
 *
 * The command is in the path, not the body, because that is how the API declares
 * which capability it needs — a body field would mean one capability for all
 * four commands. The console follows the same shape rather than inventing its
 * own.
 */
export const startRun = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  command: RunCommand,
  variables: Readonly<Record<string, unknown>>,
  /**
   * Which region to deploy into (IM-91).
   *
   * Top level rather than inside `variables`, because it is not a Terraform
   * variable — the installer takes its region from `account_config`, so one
   * sent among the variables would be ignored while the run went elsewhere.
   *
   * Omitted for a run against an existing deployment: the API takes that
   * deployment's own region and refuses a contradicting one, so sending
   * anything here would be the console holding a second opinion about where a
   * customer's instance lives.
   */
  region?: string,
  /**
   * How long this instance is meant to live (IM-134).
   *
   * Top level like the region and for the same reason: the installer has never
   * heard of it, so one sent among the variables would be ignored — and this is
   * a fact the control plane records about the instance rather than something
   * Terraform acts on.
   *
   * Only sent for a run that creates a deployment. Creation is the one moment
   * the person knows the answer; afterwards it is changed on the instance's own
   * screen, which is also where somebody can see what it currently says.
   */
  lifetime?: { days: number } | { until: string } | { indefinite: true },
  /**
   * Another build of the region's installer, for this run (IM-197).
   *
   * A tag, never an image — the repository is the region's own — and top
   * level for the region's reason. Sent only when it differs from the
   * region's tag, which is what the form decides with `runInstaller`.
   */
  installerTag?: string,
): Promise<RunSummary> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(
      accountKey,
    )}/runs/${command}`,
    {
      method: 'POST',
      body: JSON.stringify({
        variables,
        ...(region === undefined ? {} : { region }),
        ...(lifetime === undefined ? {} : { lifetime }),
        ...(installerTag === undefined ? {} : { installerTag }),
      }),
    },
  )

/**
 * Whether that run could succeed, before it is started (IM-36).
 *
 * The same body as `startRun`, because the answer is about the run the button
 * would start. It reads into the customer's account, so it is asked only from
 * a run form somebody has opened.
 */
export const fetchPreflight = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  command: RunCommand,
  variables: Readonly<Record<string, unknown>>,
  region?: string,
): Promise<PreflightResult> =>
  send(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(
      accountKey,
    )}/runs/${command}/preflight`,
    {
      method: 'POST',
      body: JSON.stringify({ variables, ...(region === undefined ? {} : { region }) }),
    },
  )

export const fetchRun = (baseUrl: string, tenantId: string, runId: string): Promise<RunSummary> =>
  request(baseUrl, `/tenants/${encodeURIComponent(tenantId)}/runs/${encodeURIComponent(runId)}`)

export interface RunLogPage {
  readonly runId: string
  readonly lines: readonly { readonly at: string; readonly message: string }[]
  readonly next?: string
  /** Why there is nothing to show, when there is nothing to show. */
  readonly note?: string
}

/**
 * Reads a page of a run's log.
 *
 * Already redacted where it was written, in the customer's account — this is a
 * reader, and by the time a line reaches it the rules have long since run.
 */
export const fetchRunLog = (
  baseUrl: string,
  tenantId: string,
  runId: string,
  after?: string,
  /**
   * Read the newest page instead of the oldest.
   *
   * For a failed run, which is the only kind whose interesting line is at the
   * end. `after` is ignored when this is set — the two are different ways of
   * choosing a page and combining them has no meaning.
   */
  fromTail?: boolean,
  /**
   * How many events one page may hold (IM-166). The API clamps it to what the
   * cloud accepts; absent is a poll's screenful. The full log reads at the
   * ceiling, because a 62,000-line apply is 125 cross-account reads at the
   * default and about seven at 10,000.
   */
  limit?: number,
): Promise<RunLogPage> => {
  const params = new URLSearchParams()
  if (fromTail === true) params.set('tail', '1')
  else if (after !== undefined) params.set('after', after)
  if (limit !== undefined) params.set('limit', String(limit))
  const encoded = params.toString()
  const query = encoded === '' ? '' : `?${encoded}`
  return request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/runs/${encodeURIComponent(runId)}/logs${query}`,
  )
}

export type RunSummaryResponse =
  | {
      readonly runId: string
      readonly summary: RunLogSummary
      readonly stored: {
        readonly readAt: string
        readonly eventsRead: number
        readonly wholeLog: boolean
      }
    }
  | { readonly runId: string; readonly summary: null; readonly note: string }

/**
 * What a finished run's log says it did (IM-35, IM-165).
 *
 * The first read of a finished run reads its whole log in the customer's
 * account; every read after that is answered from Qrvey's own table. So it is
 * asked for on the run's own screen, which somebody opened on purpose — never
 * from a list, and never for a run still going, whose summary the live panel
 * builds from the lines it is already fetching.
 */
export const fetchRunSummary = (
  baseUrl: string,
  tenantId: string,
  runId: string,
): Promise<RunSummaryResponse> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/runs/${encodeURIComponent(runId)}/summary`,
  )

/** What a tenant's approval rule is, and what its customer chose (IM-102). */
export interface ApprovalSettingsWire {
  /** `customer` or `protected-destroys`. */
  readonly policy: string
  /** In effect now: the customer's choice, or the default. */
  readonly autoApprove: readonly string[]
  /** What the customer chose, or null if they have chosen nothing. */
  readonly chosen: {
    readonly autoApprove: readonly string[]
    readonly at: string
    readonly by: string
  } | null
  readonly defaultAutoApprove: readonly string[]
  /**
   * Whether anybody at the customer could approve now (IM-176): `available`,
   * `none-assigned` or `sign-in-off`. Absent for a Qrvey tenant, and from an
   * API older than IM-176 — read then as `available`, which is what it did.
   */
  readonly customerApprover?: string
  readonly neverAutoApproved: { readonly command: string; readonly reason: string }
}

export interface ApprovalsWire extends ApprovalSettingsWire {
  readonly pending: readonly {
    readonly run: RunSummary
    /** The newest plan against the same instance, so the approver can read it. */
    readonly latestPlan?: {
      readonly runId: string
      readonly state: string
      readonly requestedAt: string
      readonly requestedBy: string
    }
    /**
     * The instance's standing protection, as of this read (IM-174) — not as of
     * the request, since a mark can be lifted while the destroy waits.
     */
    readonly protected?: { readonly at: string; readonly by: string }
  }[]
  /** Recently decided, newest first. */
  readonly decided: readonly RunSummary[]
}

const tenantPath = (tenantId: string): string => `/tenants/${encodeURIComponent(tenantId)}`

export const fetchApprovals = (baseUrl: string, tenantId: string): Promise<ApprovalsWire> =>
  request(baseUrl, `${tenantPath(tenantId)}/approvals`)

export const approveRun = (baseUrl: string, tenantId: string, runId: string): Promise<RunSummary> =>
  send(baseUrl, `${tenantPath(tenantId)}/runs/${encodeURIComponent(runId)}/approve`, {
    method: 'POST',
  })

export const rejectRun = (
  baseUrl: string,
  tenantId: string,
  runId: string,
  reason?: string,
): Promise<RunSummary> =>
  send(baseUrl, `${tenantPath(tenantId)}/runs/${encodeURIComponent(runId)}/reject`, {
    method: 'POST',
    body: JSON.stringify(reason === undefined || reason.trim() === '' ? {} : { reason }),
  })

/** One answer per run: a batch approves what it can and says why it did not. */
export interface BulkApprovalWire {
  readonly results: readonly {
    readonly runId: string
    readonly status: number
    readonly error?: string
    readonly detail?: string
  }[]
}

export const approveRuns = (
  baseUrl: string,
  tenantId: string,
  runIds: readonly string[],
): Promise<BulkApprovalWire> =>
  send(baseUrl, `${tenantPath(tenantId)}/approvals/approve`, {
    method: 'POST',
    body: JSON.stringify({ runIds }),
  })

export const fetchApprovalSettings = (
  baseUrl: string,
  tenantId: string,
): Promise<ApprovalSettingsWire> => request(baseUrl, `${tenantPath(tenantId)}/approvals/settings`)

export const setApprovalSettings = (
  baseUrl: string,
  tenantId: string,
  autoApprove: readonly string[],
): Promise<ApprovalSettingsWire> =>
  send(baseUrl, `${tenantPath(tenantId)}/approvals/settings`, {
    method: 'POST',
    body: JSON.stringify({ autoApprove }),
  })

/** Protecting one of Qrvey's own instances, or removing it (IM-39). */
export const setProtection = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
  protect: boolean,
): Promise<unknown> =>
  send(
    baseUrl,
    `${tenantPath(tenantId)}/accounts/${encodeURIComponent(accountKey)}/deployments/${encodeURIComponent(deploymentId)}/protection`,
    { method: protect ? 'POST' : 'DELETE' },
  )

/**
 * The grant that lets a scoped deploy role reach an instance's state where it
 * already is (IM-214). Built from the instance's record; asked only when
 * somebody downloads it.
 */
export interface StateAccess {
  readonly deploymentId: string
  readonly location: { readonly bucket: string; readonly key: string } | null
  readonly needed: boolean
  readonly policyName?: string
  /** Why the recorded location cannot be granted exactly; no artifact then. */
  readonly problem?: string
  readonly artifact?: { readonly filename: string; readonly document: unknown }
}

export const fetchStateAccess = (
  baseUrl: string,
  tenantId: string,
  accountKey: string,
  deploymentId: string,
): Promise<StateAccess> =>
  request(
    baseUrl,
    `/tenants/${encodeURIComponent(tenantId)}/accounts/${encodeURIComponent(accountKey)}/deployments/${encodeURIComponent(deploymentId)}/state-access`,
  )
